import React, { useState, useEffect, useRef } from 'react'
import { apiHandler } from '../../api/apihandler';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Link, useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom'
import { NOTIFICATION_COUNT, GET_CONNECTED_USERS, GENERAL_SETTINGS, AI_CHAT_START, TEACHER_MODULE_PERMISSIONS } from "../../api/constants";
import { io } from "socket.io-client";
import Header from '../../components/partials/dashboard/HeaderStyle/header'
import RightSidebar from '../../components/partials/dashboard/SidebarStyle/rightsidebar'
import Sidebar from '../../components/partials/dashboard/SidebarStyle/sidebar'
import Footer from '../../components/partials/dashboard/FooterStyle/footer'
import {
    canTeacherAccessPath,
    clearStoredTeacherModulePermissionData,
    getStoredTeacherModulePermissionData,
    getTeacherPermissionFallbackPath,
    isTeacherLoginProfile,
    setStoredTeacherModulePermissionData,
} from '../../utilities/teacherModulePermissions';

// Redux Selector / Action
import { useDispatch, useSelector } from 'react-redux';
import { setIsOptimizing, setGeneralSettings as setGeneralSettingsRedux } from '../../store/setting/actions'
//default 
// import DefaultRouter from '../../router/default-router'

// share-offcanvas
// import ShareOffcanvas from '../../components/share-offcanvas'

//settingoffCanvas
import SettingOffCanvas from '../../components/setting/SettingOffCanvas'
import DefaultHeader from '../../components/partials/dashboard/HeaderStyle/defaultHeader';

const Default = () => {
    const dispatch = useDispatch()
    let location = useLocation();
    const navigate = useNavigate();
    const socketRef = useRef(null);
    let profileData = (useSelector((state) => state.setting.setting.user_data))
    profileData = profileData ? JSON.parse(profileData) : "";

    const [notificationCount, setNotificationCount] = useState(0);
    const [onlineUserList, setOnlineUserList] = useState([]);
    const [generalSettings, setGeneralSettings] = useState('');
    const [teacherPermissionData, setTeacherPermissionData] = useState(getStoredTeacherModulePermissionData());
    const [teacherPermissionsLoaded, setTeacherPermissionsLoaded] = useState(false);

    const [showAIChatModal, setShowAIChatModal] = useState(false);

    const authToken = localStorage.getItem("uzoneAuthToken");
    const publicExactPaths = [
        '/',
        '/privacy-policy',
        '/about-us',
        '/contact-us',
        '/terms-of-use',
        '/dashboard/app/public-blog',
        '/dashboard/app/publicEvents',
    ];
    const publicPathPrefixes = [
        '/dashboard/blog/publicBlogDetails/',
        '/dashboards/app/publicEventDetails/',
    ];
    const isPublicPath = publicExactPaths.includes(location.pathname)
        || publicPathPrefixes.some((prefix) => location.pathname.startsWith(prefix));
    // About Us / Privacy Policy / Terms of Use / Contact Us are reachable both
    // signed out (public marketing pages, via isPublicPath above) and signed in
    // (e.g. from the dashboard footer) — once signed in they should look like
    // every other dashboard screen, sidebars included, instead of the bare
    // public layout. Only the root path "/" stays sidebar-less while signed in,
    // since it's the landing page rather than a dashboard screen.
    const isRootPath = location.pathname === '/';

    useEffect(() => {
        socketRef.current = io(process.env.REACT_APP_API_BASE_URL);

        return () => {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        getGeneralSettings()
        if (localStorage.getItem("uzoneAuthToken")) {
            getNotificationCount()
            getOnlineUserListApi()
            socketLogedInSetup()
            loadTeacherModulePermissions()
            if ((profileData?.user_setting?.is_influencer != 1 || profileData?.user_setting?.is_dance_corner != 1 ||
                profileData?.user_type != 2 || profileData?.user_type != 4
            ) && profileData?.user_setting?.user_mood > 3
            ) {
                getAIChatStartApi()
            }
        } else {
            clearStoredTeacherModulePermissionData()
            setTeacherPermissionData(null)
            setTeacherPermissionsLoaded(false)
        }
    }, []);

    // Add/remove body class so CSS knows whether right sidebar is rendered
    useEffect(() => {
        const hasRightSidebar =
            !!localStorage.getItem('uzoneAuthToken') &&
            !isRootPath &&
            profileData?.user_type !== 4;
        if (hasRightSidebar) {
            document.body.classList.remove('no-right-sidebar');
        } else {
            document.body.classList.add('no-right-sidebar');
        }
        return () => document.body.classList.remove('no-right-sidebar');
    }, [profileData?.user_type, isRootPath]);

    useEffect(() => {
        if (!localStorage.getItem("uzoneAuthToken")) {
            return;
        }

        if (!profileData?.id) {
            return;
        }

        loadTeacherModulePermissions();
    }, [profileData?.id]);

    useEffect(() => {
        if (!localStorage.getItem("uzoneAuthToken")) {
            return;
        }

        if (!isTeacherLoginProfile(profileData)) {
            return;
        }

        if (!teacherPermissionsLoaded) {
            return;
        }

        const canAccess = canTeacherAccessPath(location.pathname, profileData, teacherPermissionData);
        if (!canAccess) {
            const fallbackPath = getTeacherPermissionFallbackPath(teacherPermissionData);
            if (fallbackPath && fallbackPath !== location.pathname) {
                navigate(fallbackPath, { replace: true });
            }
        }
    }, [location.pathname, navigate, profileData, teacherPermissionData, teacherPermissionsLoaded]);

    useEffect(() => {
        const handleNotificationCountRefresh = (e) => {
            if (localStorage.getItem("uzoneAuthToken")) {
                // Apply the delta (if the caller knows one, e.g. "-1" for a
                // single notification just marked read) straight away so the
                // badge updates instantly instead of waiting on the round
                // trip below, then reconcile with the server in the
                // background in case anything else changed it meanwhile.
                const delta = e?.detail?.delta;
                if (typeof delta === 'number' && delta !== 0) {
                    setNotificationCount((prev) => Math.max(0, prev + delta));
                }
                getNotificationCount();
            }
        };

        window.addEventListener('notification-count-refresh', handleNotificationCountRefresh);

        return () => {
            window.removeEventListener('notification-count-refresh', handleNotificationCountRefresh);
        };
    }, []);

    function socketLogedInSetup() {
        const socket = socketRef.current;
        if (!socket) {
            return;
        }

        const userId = profileData?.id;
        socket.removeAllListeners('connect');
        socket.removeAllListeners('disconnect');
        socket.removeAllListeners('error');
        socket.removeAllListeners('notificationUpdate');
        socket.removeAllListeners('logedinusers');
        socket.removeAllListeners('userlogedout');
        socket.removeAllListeners('mediaOptimized');

        // Listen for events from the server
        socket.on('connect', () => {
            // console.log('Connected to the server');
            socket.emit("login", userId);
        });

        // Listen for events from the server
        socket.on('disconnect', () => {
            socket.emit("logedout", userId);
            // console.log('DisConnected from the server');
        });
        socket.on('error', () => {
            console.log('DisConnected from the server');
        });

        socket.on('notificationUpdate', (message) => {
            // The server pushes the authoritative unread count whenever
            // anything changes (new notification, read elsewhere, etc).
            // When it goes up, a brand new notification arrived - let any
            // open Notification page know so it can pull the new item(s)
            // in immediately instead of only refreshing on next visit.
            setNotificationCount((prev) => {
                if (typeof message === 'number' && message > prev) {
                    window.dispatchEvent(new Event('notification-new'));
                }
                return message;
            });
        });
        socket.on('logedinusers', (message) => {
            // console.log('logedin users list:', message);
            getOnlineUserListApi()
        });
        socket.on('userlogedout', (message) => {
            // console.log('logedin users list:', message);
            getOnlineUserListApi()
        });
        socket.on('mediaOptimized', (message) => {
            console.log('optimized:', message);
            dispatch(setIsOptimizing(true));
        });
    }

    async function getGeneralSettings() {
        try {
            const res = await apiHandler(GENERAL_SETTINGS, "POST", {
            })
            if (res.data?.response?.status === 200) {
                setGeneralSettings(res?.data?.data?.generalSettings)
                dispatch(setGeneralSettingsRedux(JSON.stringify(res?.data?.data?.generalSettings)));
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function getNotificationCount() {
        try {
            const res = await apiHandler(NOTIFICATION_COUNT, "POST")
            if (res.data?.response?.status === 200) {
                setNotificationCount(res?.data?.data?.count)
                //console.log('notification Count:', res?.data?.data?.count);
            } else {
                //addToast(res?.response?.data?.data?.message, { appearance: 'error' });
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    }

    async function getOnlineUserListApi() {
        try {
            const res = await apiHandler(GET_CONNECTED_USERS, 'POST', {
                is_online: '1'
            });
            if (res.data?.response?.status === 200) {
                setOnlineUserList(res?.data?.data?.users?.data)
            } else {
                setOnlineUserList([])
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    }

    async function getAIChatStartApi() {
        try {
            const res = await apiHandler(AI_CHAT_START, 'POST');
            if (res.data?.response?.status === 200) {
                setShowAIChatModal(true);
            } else {
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    }

    async function loadTeacherModulePermissions() {
        setTeacherPermissionsLoaded(false);
        try {
            if (!isTeacherLoginProfile(profileData)) {
                clearStoredTeacherModulePermissionData();
                setTeacherPermissionData(null);
                return;
            }

            const res = await apiHandler(TEACHER_MODULE_PERMISSIONS, 'GET');
            if (res?.data?.response?.status === 200 && res?.data?.data) {
                setStoredTeacherModulePermissionData(res.data.data);
                setTeacherPermissionData(getStoredTeacherModulePermissionData());
            } else {
                clearStoredTeacherModulePermissionData();
                setTeacherPermissionData(null);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setTeacherPermissionsLoaded(true);
        }
    }

    if (!authToken && !isPublicPath) {
        return <Navigate to="/auth/sign-in" replace />;
    }

    return (
        <>
            {
                localStorage.getItem("uzoneAuthToken") ?
                    isRootPath ? null :
                        <Sidebar /> : null
            }
            <Header notificationCount={notificationCount} />
            {/* {
                localStorage.getItem("uzoneAuthToken") ?
                    <Header notificationCount={notificationCount} /> :
                    <DefaultHeader />
            } */}
            {/* AnnouncementBanner now lives inside Header itself, stacked with
                the Registration Packet warning in the one measured sticky
                slot the sidebar/right-sidebar's fixed positioning depends on
                (see header.js's bannerRef comment) - mounting it here too,
                outside that system, is what caused it to overlap the
                sidebars and render edge-to-edge instead of within the
                content width. */}
            <div className="main-content">
                {/* <div id="content-page" className="content-page"> */}
                {/* <DefaultRouter/> */}
                <Outlet />
                {/* </div> */}
            </div>
            {
                localStorage.getItem("uzoneAuthToken") ?
                    isRootPath ? null :
                        profileData?.user_type === 4 ? null :
                            <RightSidebar onlineUserList={onlineUserList} /> : null
            }
            {
                // footer-shell-inset mirrors .sidebar ~ .main-content's own
                // sidebar-aware margin-left/padding-right (see _footer.scss)
                // via a real ≥1200px media query, instead of the JS
                // innerWidth<768 check + hardcoded 72% this replaced — that
                // combination squeezed the footer to 72% width at every size
                // down to 768px, well below 1200px where the sidebar is
                // actually just a hidden off-canvas drawer taking no layout
                // space. Root path never renders a Sidebar (see above), so it
                // skips the inset the same way it skips the Sidebar itself.
                localStorage.getItem("uzoneAuthToken") ?
                    <div className={`footer-shell${isRootPath ? '' : ' footer-shell-inset'}`}>
                        <Footer />
                    </div> :
                    <div className="footer-shell">
                        <Footer />
                    </div>
            }

            <Modal
                show={showAIChatModal}
                onHide={() => setShowAIChatModal(false)}
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body className="text-center p-4" style={{ borderRadius: 16 }}>
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/4712/4712035.png"
                        alt="AI Friend"
                        style={{ width: 80, marginBottom: 16 }}
                    />
                    <h4 style={{ fontWeight: 700, color: "#1976d2" }}>You have one friend waiting to talk to you.</h4>
                    <p style={{ color: "#555", marginTop: 12, marginBottom: 24 }}>
                        Would you like to start a conversation with your AI Counsellor now?
                    </p>
                    <div className="d-flex justify-content-center gap-3">
                        <Button
                            variant="primary"
                            style={{ minWidth: 100, fontWeight: 600 }}
                            onClick={() => {
                                setShowAIChatModal(false);
                                window.open(`/chat/index?type=user&slug=${"ai-chat"}`, '_blank');
                            }}
                        >
                            Yes
                        </Button>
                        <Button
                            variant="outline-secondary"
                            style={{ minWidth: 100, fontWeight: 600 }}
                            onClick={() => setShowAIChatModal(false)}
                        >
                            Not Now
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
            {/* <SettingOffCanvas /> */}
        </>
    )
}

export default Default
